/**
 * Widget Storage
 *
 * Namespaced localStorage wrapper for multi-tenant widget deployments.
 * Each widget instance can have isolated storage using tenant/instance prefixes.
 *
 * Key features:
 * - Namespaced keys to prevent collisions
 * - JSON serialization/deserialization
 * - TTL support for expiring values
 * - Safe fallback when localStorage unavailable
 * - Session storage option for sensitive data
 */

// ============================================================================
// Types
// ============================================================================

/** Storage options */
export interface StorageOptions {
  /** Namespace prefix for all keys (e.g., 'companion' or 'companion-tenant123') */
  namespace?: string;
  /** Use sessionStorage instead of localStorage */
  useSessionStorage?: boolean;
}

/** Options for setting a value */
export interface SetOptions {
  /** Time-to-live in milliseconds (optional) */
  ttl?: number;
}

/** Stored value wrapper with metadata */
interface StoredValue<T> {
  value: T;
  /** Expiration timestamp (ms since epoch), undefined = no expiration */
  expiresAt?: number;
  /** Storage version for future migrations */
  version: number;
}

// ============================================================================
// Constants
// ============================================================================

/** Current storage format version */
const STORAGE_VERSION = 1;

/** Default namespace */
const DEFAULT_NAMESPACE = 'companion-widget';

/** Known storage keys (for type safety and documentation) */
export const STORAGE_KEYS = {
  /** Anonymous client ID for session continuity */
  ANONYMOUS_ID: 'anon-id',
  /** Authenticated session token */
  SESSION_TOKEN: 'session-token',
  /** Session token expiration timestamp */
  TOKEN_EXPIRES_AT: 'token-expires',
  /** Whether the session is authenticated (not anonymous) */
  IS_AUTHENTICATED: 'is-auth',
  /** Authenticated user ID */
  USER_ID: 'user-id',
  /** Refresh token for session renewal */
  REFRESH_TOKEN: 'refresh-token',
  /** Whether tooltip has been shown (for showOnlyOnce) */
  TOOLTIP_SHOWN: 'tooltip-shown',
  /** Conversation history (if persistConversation enabled) */
  CONVERSATION: 'conversation',
  /** User theme preference */
  THEME_PREFERENCE: 'theme',
  /** Widget open/closed state */
  WIDGET_STATE: 'widget-state',
  /** Prefix for experiment variant assignments (keys: exp:{experimentName}) */
  EXPERIMENT_PREFIX: 'exp',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

// ============================================================================
// Storage Availability Check
// ============================================================================

/**
 * Check if localStorage/sessionStorage is available.
 * Some browsers block storage in private mode or when cookies are disabled.
 */
function isStorageAvailable(type: 'localStorage' | 'sessionStorage'): boolean {
  try {
    const storage = window[type];
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// WidgetStorage Class
// ============================================================================

/**
 * Namespaced storage wrapper for widget data.
 *
 * Usage:
 * ```typescript
 * // Create storage with default namespace
 * const storage = new WidgetStorage();
 *
 * // Create storage with tenant-specific namespace
 * const storage = new WidgetStorage({ namespace: 'companion-tenant123' });
 *
 * // Store a value
 * storage.set(STORAGE_KEYS.ANONYMOUS_ID, 'abc-123');
 *
 * // Store with TTL (expires in 1 hour)
 * storage.set(STORAGE_KEYS.SESSION_TOKEN, 'token', { ttl: 60 * 60 * 1000 });
 *
 * // Retrieve a value
 * const id = storage.get<string>(STORAGE_KEYS.ANONYMOUS_ID);
 *
 * // Remove a value
 * storage.remove(STORAGE_KEYS.SESSION_TOKEN);
 *
 * // Clear all widget storage
 * storage.clear();
 * ```
 */
export class WidgetStorage {
  private namespace: string;
  private storage: Storage | null;
  private memoryFallback: Map<string, string>;

  constructor(options: StorageOptions = {}) {
    this.namespace = options.namespace ?? DEFAULT_NAMESPACE;
    this.memoryFallback = new Map();

    // Determine which storage to use
    const storageType = options.useSessionStorage ? 'sessionStorage' : 'localStorage';
    this.storage = isStorageAvailable(storageType) ? window[storageType] : null;
  }

  /**
   * Build the full storage key with namespace.
   */
  private buildKey(key: string): string {
    return `${this.namespace}:${key}`;
  }

  /**
   * Get the underlying storage (with fallback).
   */
  private getStorage(): Storage | Map<string, string> {
    return this.storage ?? this.memoryFallback;
  }

  /**
   * Set a value in storage.
   *
   * @param key - Storage key (use STORAGE_KEYS constants)
   * @param value - Value to store (will be JSON serialized)
   * @param options - Optional settings like TTL
   */
  set<T>(key: string, value: T, options: SetOptions = {}): void {
    const fullKey = this.buildKey(key);
    const stored: StoredValue<T> = {
      value,
      version: STORAGE_VERSION,
    };

    if (options.ttl && options.ttl > 0) {
      stored.expiresAt = Date.now() + options.ttl;
    }

    try {
      const serialized = JSON.stringify(stored);
      const storage = this.getStorage();

      if (storage instanceof Map) {
        storage.set(fullKey, serialized);
      } else {
        storage.setItem(fullKey, serialized);
      }
    } catch (error) {
      // Storage quota exceeded or other error - fail silently
      // In production, you might want to log this
      console.warn('[WidgetStorage] Failed to set value:', error);
    }
  }

  /**
   * Get a value from storage.
   *
   * @param key - Storage key
   * @returns The stored value, or undefined if not found/expired
   */
  get<T>(key: string): T | undefined {
    const fullKey = this.buildKey(key);

    try {
      const storage = this.getStorage();
      const serialized = storage instanceof Map
        ? storage.get(fullKey)
        : storage.getItem(fullKey);

      if (!serialized) {
        return undefined;
      }

      const stored = JSON.parse(serialized) as StoredValue<T>;

      // Check expiration
      if (stored.expiresAt && Date.now() > stored.expiresAt) {
        this.remove(key);
        return undefined;
      }

      return stored.value;
    } catch {
      // Parse error or other issue
      return undefined;
    }
  }

  /**
   * Remove a value from storage.
   *
   * @param key - Storage key
   */
  remove(key: string): void {
    const fullKey = this.buildKey(key);
    const storage = this.getStorage();

    if (storage instanceof Map) {
      storage.delete(fullKey);
    } else {
      storage.removeItem(fullKey);
    }
  }

  /**
   * Check if a key exists and is not expired.
   *
   * @param key - Storage key
   */
  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  /**
   * Clear all storage for this namespace.
   */
  clear(): void {
    const storage = this.getStorage();

    if (storage instanceof Map) {
      // Clear only keys with our namespace
      for (const key of storage.keys()) {
        if (key.startsWith(`${this.namespace}:`)) {
          storage.delete(key);
        }
      }
    } else {
      // localStorage/sessionStorage - iterate and remove namespaced keys
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(`${this.namespace}:`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => storage.removeItem(key));
    }
  }

  /**
   * Get all keys in this namespace.
   */
  keys(): string[] {
    const storage = this.getStorage();
    const prefix = `${this.namespace}:`;
    const result: string[] = [];

    if (storage instanceof Map) {
      for (const key of storage.keys()) {
        if (key.startsWith(prefix)) {
          result.push(key.slice(prefix.length));
        }
      }
    } else {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(prefix)) {
          result.push(key.slice(prefix.length));
        }
      }
    }

    return result;
  }

  /**
   * Get the current namespace.
   */
  getNamespace(): string {
    return this.namespace;
  }

  /**
   * Check if storage is using the memory fallback.
   */
  isUsingFallback(): boolean {
    return this.storage === null;
  }
}

// ============================================================================
// Convenience Functions
// ============================================================================

/** Default storage instance (created lazily) */
let defaultStorage: WidgetStorage | null = null;

/**
 * Get the default storage instance.
 *
 * For simple use cases where you don't need tenant isolation.
 */
export function getDefaultStorage(): WidgetStorage {
  if (!defaultStorage) {
    defaultStorage = new WidgetStorage();
  }
  return defaultStorage;
}

/**
 * Create a tenant-specific storage instance.
 *
 * @param tenantId - Tenant identifier for namespace isolation
 */
export function createTenantStorage(tenantId: string): WidgetStorage {
  return new WidgetStorage({
    namespace: `companion-${tenantId}`,
  });
}

/**
 * Create a session storage instance (clears on tab close).
 *
 * Use for sensitive data that shouldn't persist.
 */
export function createSessionStorage(namespace?: string): WidgetStorage {
  return new WidgetStorage({
    namespace: namespace ?? DEFAULT_NAMESPACE,
    useSessionStorage: true,
  });
}

/**
 * Reset the default storage instance (for testing).
 */
export function _resetDefaultStorage(): void {
  if (defaultStorage) {
    defaultStorage.clear();
    defaultStorage = null;
  }
}

// ============================================================================
// Anonymous ID Helpers
// ============================================================================

/**
 * Generate an RFC 9562 compliant UUIDv7.
 *
 * Matches the backend generate_uuidv7() in utils/identifiers.py so that
 * anonymous IDs are format-identical to authenticated user IDs when the
 * backend promotes anonymous_id → user_id on OTP verification.
 *
 * Structure (128 bits):
 * - 48 bits: Unix timestamp in milliseconds
 * - 4 bits: Version (0111 = 7)
 * - 12 bits: Cryptographic random
 * - 2 bits: Variant (10)
 * - 62 bits: Cryptographic random
 */
export function generateAnonymousId(): string {
  const now = Date.now();
  const tsHex = now.toString(16).padStart(12, '0').slice(-12);

  const rand = new Uint8Array(10);
  crypto.getRandomValues(rand);

  // rand_a: 12 random bits
  const randA = (((rand[0] & 0x0f) << 8) | rand[1]).toString(16).padStart(3, '0');

  // variant (10) + 14 random bits
  const varRandB0 = ((rand[2] & 0x3f) | 0x80).toString(16).padStart(2, '0');
  const varRandB1 = rand[3].toString(16).padStart(2, '0');

  // remaining 48 random bits
  const randBRest = Array.from(rand.slice(4), (b) => b.toString(16).padStart(2, '0')).join('');

  return [
    tsHex.slice(0, 8),
    tsHex.slice(8, 12),
    '7' + randA,
    varRandB0 + varRandB1,
    randBRest,
  ].join('-');
}

/**
 * Get or create an anonymous ID.
 *
 * Retrieves existing ID from storage or generates a new one.
 *
 * @param storage - WidgetStorage instance to use
 * @returns The anonymous ID (existing or newly generated)
 */
export function getOrCreateAnonymousId(storage: WidgetStorage): string {
  let anonId = storage.get<string>(STORAGE_KEYS.ANONYMOUS_ID);

  if (!anonId) {
    anonId = generateAnonymousId();
    storage.set(STORAGE_KEYS.ANONYMOUS_ID, anonId);
  }

  return anonId;
}

/**
 * Clear the anonymous ID (e.g., after successful authentication merge).
 *
 * @param storage - WidgetStorage instance to use
 */
export function clearAnonymousId(storage: WidgetStorage): void {
  storage.remove(STORAGE_KEYS.ANONYMOUS_ID);
}

// ============================================================================
// Onboarding Cache Helpers
// ============================================================================

/** Prefix for scoped onboarding completion keys. */
const ONBOARDING_COMPLETE_PREFIX = 'onboarding-complete';

/**
 * Build a scoped onboarding cache key.
 *
 * Key is scoped by tenant + user + bundle pointer so that:
 * - Different tenants don't share onboarding state
 * - Sign-out and re-login re-triggers onboarding
 * - Bundle updates (new bundlePointer) re-trigger onboarding
 */
export function buildOnboardingKey(
  tenantPublicId: string,
  userId: string,
  bundlePointer: string,
): string {
  return `${ONBOARDING_COMPLETE_PREFIX}:${tenantPublicId}:${userId}:${bundlePointer}`;
}

/**
 * Check if onboarding is complete for the given scope.
 */
export function getOnboardingComplete(
  storage: WidgetStorage,
  tenantPublicId: string,
  userId: string,
  bundlePointer: string,
): boolean {
  const key = buildOnboardingKey(tenantPublicId, userId, bundlePointer);
  return storage.get<boolean>(key) === true;
}

/**
 * Mark onboarding as complete for the given scope.
 */
export function setOnboardingComplete(
  storage: WidgetStorage,
  tenantPublicId: string,
  userId: string,
  bundlePointer: string,
): void {
  const key = buildOnboardingKey(tenantPublicId, userId, bundlePointer);
  storage.set(key, true);
}

/**
 * Clear all onboarding completion keys (e.g., on logout or identity reset).
 * Iterates storage keys and removes any that match the onboarding prefix.
 */
export function clearOnboardingComplete(storage: WidgetStorage): void {
  const allKeys = storage.keys();
  for (const key of allKeys) {
    if (key.startsWith(ONBOARDING_COMPLETE_PREFIX)) {
      storage.remove(key);
    }
  }
}
